import React from 'react';
import Icon from '../../../components/AppIcon';

const ConceptSection = () => {
  const studentBenefits = [
    { icon: 'Search', text: 'Discover institutes that match your goals and budget' },
    { icon: 'MessageCircle', text: 'Chat directly with institute counselors in real time' },
    { icon: 'Video', text: 'Book 1-on-1 video counseling sessions from home' },
    { icon: 'BookOpen', text: 'Explore courses, fees, placements and campus life' },
  ];

  const instituteBenefits = [
    { icon: 'Users', text: 'Reach thousands of motivated students in one place' },
    { icon: 'LayoutDashboard', text: 'Manage your virtual booth, FAQs and course catalog' },
    { icon: 'Bot', text: 'AI-powered chat handles student queries 24/7' },
    { icon: 'BarChart2', text: 'Track visitor engagement and leads in real time' },
  ];

  const steps = [
    {
      step: '01',
      icon: 'UserPlus',
      title: 'Student Registers',
      description: 'A student signs up, gets approved, and enters the virtual expo.',
      gradient: 'from-cyan-500 to-blue-500',
      bg: 'from-cyan-50 to-blue-50',
    },
    {
      step: '02',
      icon: 'Building2',
      title: 'Explores Virtual Booths',
      description: 'Browse institute booths — courses, fees, placements, campus life — all in one place.',
      gradient: 'from-violet-500 to-purple-500',
      bg: 'from-violet-50 to-purple-50',
    },
    {
      step: '03',
      icon: 'MessageCircle',
      title: 'Connects & Asks',
      description: 'Chat with AI or live counselors, ask questions, and get instant answers.',
      gradient: 'from-pink-500 to-rose-500',
      bg: 'from-pink-50 to-rose-50',
    },
    {
      step: '04',
      icon: 'CalendarCheck',
      title: 'Books a Session',
      description: 'Schedule a personal video counseling session with the institute of choice.',
      gradient: 'from-amber-500 to-orange-500',
      bg: 'from-amber-50 to-orange-50',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 space-y-20">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-violet-600 bg-violet-50 rounded-full mb-4">
            How It Works
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Bridging Students &amp; Institutions — Digitally
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            StudyExpo is a virtual education fair where students explore, connect, and make informed decisions — and institutions reach the right students without the cost of physical events.
          </p>
        </div>

        {/* Two-sided value proposition */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Students side */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8 border border-cyan-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-md">
                <Icon name="GraduationCap" size={24} color="white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-cyan-600">For Students</p>
                <h3 className="text-xl font-heading font-bold text-foreground">Your Dream College, One Click Away</h3>
              </div>
            </div>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              No more traveling to multiple college fairs. Explore every institute from your home, get your questions answered instantly, and make a confident admission decision.
            </p>
            <ul className="space-y-3">
              {studentBenefits?.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                    <Icon name={item?.icon} size={16} className="text-cyan-600" color="#0891b2" />
                  </div>
                  <span className="text-sm text-foreground leading-relaxed">{item?.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Institutions side */}
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl p-8 border border-violet-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-md">
                <Icon name="Building2" size={24} color="white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-violet-600">For Institutions</p>
                <h3 className="text-xl font-heading font-bold text-foreground">Your Virtual Booth, Always Open</h3>
              </div>
            </div>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Set up your virtual booth once and engage with thousands of prospective students year-round — no logistics, no travel, no venue costs. Just meaningful connections.
            </p>
            <ul className="space-y-3">
              {instituteBenefits?.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                    <Icon name={item?.icon} size={16} color="#7c3aed" />
                  </div>
                  <span className="text-sm text-foreground leading-relaxed">{item?.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Engagement Flow */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
              The Student–Institution Journey
            </h3>
            <p className="text-muted-foreground max-w-xl mx-auto">
              From first discovery to final decision — every step happens right here on StudyExpo.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps?.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector line */}
                {index < steps?.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%-12px)] w-6 h-0.5 bg-gradient-to-r from-gray-300 to-gray-200 z-10" />
                )}
                <div className={`bg-gradient-to-br ${step?.bg} border border-white rounded-2xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 h-full`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${step?.gradient} rounded-xl flex items-center justify-center shadow-md`}>
                      <Icon name={step?.icon} size={22} color="white" />
                    </div>
                    <span className={`text-2xl font-black bg-gradient-to-r ${step?.gradient} bg-clip-text text-transparent`}>
                      {step?.step}
                    </span>
                  </div>
                  <h4 className="text-base font-bold text-foreground mb-2">{step?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Central value statement */}
        <div className="bg-gradient-to-r from-cyan-600 to-violet-600 rounded-3xl p-10 md:p-14 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          </div>
          <div className="relative">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon name="Handshake" size={32} color="white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">
              One Platform. Two Worlds. Infinite Possibilities.
            </h3>
            <p className="text-white/85 text-lg max-w-2xl mx-auto leading-relaxed">
              StudyExpo eliminates the gap between students searching for the right college and institutions looking for the right students. Real conversations. Real decisions. No boundaries.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ConceptSection;